import { useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react'
import emailjs from '@emailjs/browser'

const contactInfo = [
  {
    icon: Mail,
    label: 'Email',
    value: 'info@kvstudio.hu',
    href: 'mailto:info@kvstudio.hu',
  },
  {
    icon: Phone,
    label: 'Telefon',
    value: '+36 30 123 4567',
    href: 'tel:+36301234567',
  },
  {
    icon: MapPin,
    label: 'Helyszín',
    value: 'Budapest, Magyarország',
    href: null,
  },
]

const Contact = () => {
  const formRef = useRef()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (isSubmitting) return
    
    setIsSubmitting(true)
    setSubmitStatus(null)

    // EmailJS configuration - using Vite env variables
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID'
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID'
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY'

    try {
      // Check if EmailJS is configured
      if (serviceId === 'YOUR_SERVICE_ID') {
        // Demo mode - simulate successful submission
        await new Promise(resolve => setTimeout(resolve, 1500))
        setSubmitStatus('success')
        formRef.current.reset()
        console.log('Form submitted (demo mode):', {
          name: formRef.current.user_name.value,
          email: formRef.current.user_email.value,
          phone: formRef.current.user_phone.value,
          message: formRef.current.message.value,
        })
      } else {
        // Production mode - use EmailJS
        await emailjs.sendForm(serviceId, templateId, formRef.current, publicKey)
        setSubmitStatus('success')
        formRef.current.reset()
      }
      
      setTimeout(() => setSubmitStatus(null), 5000)
    } catch (error) {
      console.error('Failed to send email:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="kapcsolat" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left side - Info */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-coffee-600 text-sm font-medium tracking-widest uppercase block mb-4">
                Kapcsolat
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-coffee-900 mb-6">
                Kávézzunk egyet!
              </h2>
              <p className="text-lg text-coffee-600 leading-relaxed">
                Készen állok, hogy segítsek a vállalkozásodnak növekedni. 
                Írj, hívj, vagy töltsd ki az űrlapot – és beszéljük meg, hogyan dolgozhatunk együtt!
              </p>
            </motion.div>

            {/* Contact details */}
            <div className="space-y-6">
              {contactInfo.map((item, index) => {
                const Icon = item.icon
                return (
                  <motion.div
                    key={item.label}
                    className="flex items-start gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-12 h-12 bg-coffee-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon size={24} className="text-coffee-600" />
                    </div>
                    <div>
                      <p className="text-sm text-coffee-500 mb-1">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="text-lg font-semibold text-coffee-900 hover:text-coffee-600 transition-colors"
                          data-testid={`contact-${item.label.toLowerCase()}`}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-lg font-semibold text-coffee-900">{item.value}</p>
                      )}
                    </div>
                  </motion.div>
                )
              })}
            </div>

            {/* Social links placeholder */}
            <motion.div
              className="pt-8 border-t border-coffee-100"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-sm text-coffee-500 mb-4">Kövess a közösségi médiában</p>
              <div className="flex gap-4">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-coffee-100 rounded-xl flex items-center justify-center text-coffee-600 hover:bg-coffee-600 hover:text-white transition-colors"
                  data-testid="social-facebook"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                  </svg>
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-coffee-100 rounded-xl flex items-center justify-center text-coffee-600 hover:bg-coffee-600 hover:text-white transition-colors"
                  data-testid="social-instagram"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-coffee-100 rounded-xl flex items-center justify-center text-coffee-600 hover:bg-coffee-600 hover:text-white transition-colors"
                  data-testid="social-linkedin"
                  aria-label="LinkedIn"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </motion.div>
          </div>

          {/* Right side - Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="bg-coffee-50 rounded-3xl p-8 md:p-10 space-y-6"
            >
              <div>
                <label htmlFor="user_name" className="block text-sm font-medium text-coffee-800 mb-2">
                  Név *
                </label>
                <input
                  type="text"
                  id="user_name"
                  name="user_name"
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-white border border-coffee-200 rounded-xl text-coffee-900 placeholder-coffee-400 focus:outline-none focus:ring-2 focus:ring-coffee-600 focus:border-transparent transition-all disabled:opacity-50"
                  placeholder="A neved"
                  data-testid="contact-form-name"
                />
              </div>

              <div>
                <label htmlFor="user_email" className="block text-sm font-medium text-coffee-800 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="user_email"
                  name="user_email"
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-white border border-coffee-200 rounded-xl text-coffee-900 placeholder-coffee-400 focus:outline-none focus:ring-2 focus:ring-coffee-600 focus:border-transparent transition-all disabled:opacity-50"
                  placeholder="email@pelda.hu"
                  data-testid="contact-form-email"
                />
              </div>

              <div>
                <label htmlFor="user_phone" className="block text-sm font-medium text-coffee-800 mb-2">
                  Telefonszám
                </label>
                <input
                  type="tel"
                  id="user_phone"
                  name="user_phone"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-white border border-coffee-200 rounded-xl text-coffee-900 placeholder-coffee-400 focus:outline-none focus:ring-2 focus:ring-coffee-600 focus:border-transparent transition-all disabled:opacity-50"
                  placeholder="+36 30 123 4567"
                  data-testid="contact-form-phone"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-coffee-800 mb-2">
                  Üzenet *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="5"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-white border border-coffee-200 rounded-xl text-coffee-900 placeholder-coffee-400 focus:outline-none focus:ring-2 focus:ring-coffee-600 focus:border-transparent transition-all resize-none disabled:opacity-50"
                  placeholder="Írd le, miben segíthetek..."
                  data-testid="contact-form-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-coffee-900 text-cream px-8 py-4 rounded-xl font-semibold text-lg hover:bg-coffee-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-form-submit"
              >
                {isSubmitting ? (
                  <>
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-cream border-t-transparent rounded-full"
                    />
                    Küldés...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Üzenet küldése
                  </>
                )}
              </button>

              {/* Status messages */}
              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-green-700 bg-green-100 p-4 rounded-xl"
                  data-testid="contact-form-success"
                >
                  <CheckCircle size={20} />
                  <span>Köszönöm az üzeneted! Hamarosan felveszem veled a kapcsolatot.</span>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-red-700 bg-red-100 p-4 rounded-xl"
                  data-testid="contact-form-error"
                >
                  <AlertCircle size={20} />
                  <span>Hiba történt. Kérlek próbáld újra, vagy írj közvetlenül az email címemre.</span>
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Contact
